import Drawer from '@material-ui/core/Drawer';
import { generateCandidatePresentation } from 'features/ai-generators';
import {
  CallVerbiages,
  CandidatePresentationTypeSelector,
  CandidateRoleplayScenarioSelector,
  PresentationMakerTemplate
} from 'features/ai-generators/components';
import { CALL_VERBIAGES_TYPES } from 'features/ai-generators/constants';
import { CreationTaskDrawer } from 'features/daily-plan';
import {
  ProfileAccountableBar,
  ProfileHeaderActions,
  ProfilePageLayout,
  ProfileSummary,
  ProfileTabs
} from 'features/inventory-profiles/components/shared';
import {
  CANDIDATE_DRAWERS,
  CANDIDATE_PROFILE_CONFIG,
  CANDIDATE_TAB_IDS,
  COMMON_TAB_IDS
} from 'features/inventory-profiles/constants';
import {
  useCandidateProfile,
  useProfileAccountability,
  useProfileDrawer,
  useTabRefresh
} from 'features/inventory-profiles/hooks';
import { ProfileRestrictionDrawer, useProfileRestrictions } from 'features/profile-restrictions';
import { useSearchProjectInProfiles } from 'hooks/searchProject';
import strings from 'strings';
import { FPActionButton, When } from 'UI/components/atoms';
import { VerifyUrgencyDialog } from 'UI/components/molecules/VerifyUrgencyDialog';
import CandidateEdit from 'UI/components/organisms/CandidateEdit';
import AddHiringAuthority from 'UI/components/organisms/candidates/HiringAuthority/AddHiringAuthority';
import LinkHiringAuthorityDrawer from 'UI/components/organisms/candidates/HiringAuthority/LinkHiringAuthorityDrawer';
import ReferenceRelease from 'UI/components/organisms/candidates/ReferenceRelease';
import { ClaimAccountabilityDrawer } from 'UI/components/organisms/inventoryProfiles/ClaimAccountability/ClaimAccountabilityDrawer';
import ToggleProfileModeButton from 'UI/components/organisms/inventoryProfiles/ToggleProfileModeButton';
import { handleEntityEditionCompleted } from 'UI/components/organisms/inventoryProfiles/utils';
import LinkCompanyDialog from 'UI/components/organisms/LinkCompanyDialog';
import ReassignRecruiterForm from 'UI/components/organisms/ReassignRecruiter';
import SendoutManager from 'UI/components/organisms/sendouts';
import { CandidateEntity, EntityType, SearchProjectEntities } from 'UI/constants/entityTypes';
import { SvgMakeAi } from 'UI/res';

import { useCandidateActions, useCandidatePermissions } from './CandidateProfileV2.hooks';
import { buildCandidateTabs } from './CandidateProfileV2.tabs';
import {
  buildCandidateMenuItems,
  buildPresentationMakerCandidateParam,
  formatCandidateResponse
} from './CandidateProfileV2.utils';

/**
 * Candidate profile. Composes the data, permissions and actions hooks with the shared layout.
 * @param {Object} props
 * @param {number|string} props.id Candidate id, from the route or the quick view
 * @param {boolean} [props.enableUrlNavigation] Sync the active tab with the URL. True by default
 *   (route); the quick view passes false
 * @param {Function} [props.onEditionCompleted] Called after a profile edit in quick-view mode
 * @param {string} [props.layoutClassName] Extra className for the content area (quick view)
 * @param {Function} [props.onVerificationCompleted] Called after the Verify Urgency dialog saves
 * @param {Object} [props.profileModeProps] Forwarded to ToggleProfileModeButton
 */
export const CandidateProfileV2 = ({
  enableUrlNavigation = true,
  id,
  layoutClassName,
  onEditionCompleted,
  onVerificationCompleted,
  profileModeProps
}) => {
  const {
    candidate,
    candidateTypes,
    dataSheet,
    dataSheetResponseStatus,
    error,
    hiringName,
    isAlreadyAccountable,
    isLoading,
    refresh,
    refreshReferenceReleases,
    reload
  } = useCandidateProfile(id);
  const isProfileReady = !isLoading && !error && Boolean(candidate.id);

  const formattedCandidate = formatCandidateResponse({ candidate, dataSheet, candidateTypes });

  const permissions = useCandidatePermissions({ candidate, isAlreadyAccountable });
  const restrictions = useProfileRestrictions({
    canRequestDoNotContact: permissions.canRequestDoNotContact,
    canRequestDoNotEngage: permissions.canRequestDoNotEngage,
    entityTypeId: CANDIDATE_PROFILE_CONFIG.entityTypeId,
    profileStatusId: candidate.status?.id
  });

  const { refreshTab, getTabVersion } = useTabRefresh();
  const {
    drawer,
    drawerItemId,
    openDrawer,
    openItemDrawer,
    closeDrawer,
    closeAndRefresh,
    closeAndRefreshTab
  } = useProfileDrawer({ onChanged: refresh, onTabChanged: refreshTab });
  const assignment = useProfileAccountability({
    canEdit: permissions.canEdit,
    entity: CANDIDATE_PROFILE_CONFIG,
    profile: candidate,
    onReassign: openDrawer(CANDIDATE_DRAWERS.Recruiters)
  });

  const { SearchProjectForms, SearchProjectsMenuItems, searchProjectTabPropsDefinition } =
    useSearchProjectInProfiles({
      endpoint: `${CANDIDATE_PROFILE_CONFIG.endpoint}/${id}`,
      entity: SearchProjectEntities.Candidate,
      profile: { id: candidate.id, name: formattedCandidate.header.title },
      tabKey: COMMON_TAB_IDS.SearchProjects,
      triggerTabRefresh: refreshTab,
      shouldRedirectToSearchProjectPreview: enableUrlNavigation
    });

  const actions = useCandidateActions({
    hiringAuthorityId: candidate.relation?.hiring_authority_id,
    hiringName,
    id,
    refresh,
    refreshReferenceReleases
  });

  const tabs = buildCandidateTabs({
    candidate,
    candidateName: formattedCandidate.header.title,
    dataSheet,
    dataSheetResponseStatus,
    isLoading,
    permissions,
    openDrawer,
    openItemDrawer,
    onWriteUpClick: actions.openDataSheet,
    refresh,
    refreshTab,
    searchProjectsTab: searchProjectTabPropsDefinition
  });

  const menuItems = buildCandidateMenuItems({
    permissions,
    canManage: assignment.canManage,
    canView: assignment.canView,
    canCollaborate: assignment.canCollaborate,
    hasAssistant: assignment.hasAssistant,
    onOpenDataSheet: actions.openDataSheet,
    searchProjectMenuItems: SearchProjectsMenuItems,
    restrictionMenuItems: restrictions.menuItems,
    openDrawer,
    onOpenReferenceRelease: actions.openReferenceRelease,
    onUnlinkHiringAuthority: actions.unlinkHiringAuthority
  });

  return (
    <>
      <ProfilePageLayout
        header={formattedCandidate.header}
        hasHeaderActions={permissions.canUsePresentationMaker || permissions.hasHiringAuthority}
        actions={
          <ProfileHeaderActions menuItems={menuItems}>
            <When condition={permissions.canUsePresentationMaker}>
              <FPActionButton
                icon={SvgMakeAi}
                onClick={openDrawer(CANDIDATE_DRAWERS.Presentation)}
                text={strings.ai.generators.presentationsMaker.actionButton}
                withGradient
              />
            </When>
            <When condition={permissions.hasHiringAuthority}>
              <ToggleProfileModeButton
                results={candidate}
                mode={EntityType.Candidate}
                {...profileModeProps}
              />
            </When>
          </ProfileHeaderActions>
        }
        subheader={
          <>
            <ProfileSummary
              company={formattedCandidate.summary.company}
              location={formattedCandidate.summary.location}
              status={formattedCandidate.status}
              created={formattedCandidate.summary.created}
              details={formattedCandidate.summary.details}
            />
            <ProfileAccountableBar assignment={assignment} />
          </>
        }
        isLoading={isLoading}
        error={error}
        onRetry={reload}
        enableUrlNavigation={enableUrlNavigation}
        layoutClassName={layoutClassName}
      >
        <ProfileTabs
          tabs={tabs}
          getTabVersion={getTabVersion}
          enableUrlNavigation={enableUrlNavigation}
          organizationStorageKey={CANDIDATE_PROFILE_CONFIG.tabsStorageKey}
          organizationSubheader={CANDIDATE_PROFILE_CONFIG.tabsSubheader}
        />
      </ProfilePageLayout>

      <When condition={isProfileReady && drawer === CANDIDATE_DRAWERS.Edit && permissions.canEdit}>
        <CandidateEdit
          candidate={candidate}
          onEditCompleted={handleEntityEditionCompleted({
            handleEditCompleted: closeAndRefresh,
            enableUrlNavigation,
            onEditionCompleted
          })}
          onEditClosed={closeDrawer}
        />
      </When>
      <When condition={isProfileReady && drawer === CANDIDATE_DRAWERS.Sendout}>
        <Drawer open onClose={closeDrawer}>
          <SendoutManager
            id={drawerItemId}
            onEdit={closeAndRefreshTab(CANDIDATE_TAB_IDS.Sendouts)}
            onDelete={closeAndRefreshTab(CANDIDATE_TAB_IDS.Sendouts)}
            onClose={closeDrawer}
            onNavigate={actions.navigateFromSendout}
          />
        </Drawer>
      </When>
      <When condition={isProfileReady && drawer === CANDIDATE_DRAWERS.Recruiters}>
        <ReassignRecruiterForm
          baseEndpoint={CANDIDATE_PROFILE_CONFIG.endpoint}
          item={candidate}
          entityType={CandidateEntity}
          onReassignCompleted={closeAndRefresh}
          onReassignClosed={closeDrawer}
        />
      </When>
      <When
        condition={
          isProfileReady &&
          Boolean(actions.referenceReleaseCandidateId) &&
          permissions.canOpenReferenceRelease
        }
      >
        <ReferenceRelease
          candidateId={actions.referenceReleaseCandidateId}
          onClose={actions.closeReferenceRelease}
          onCompleted={actions.completeReferenceRelease}
        />
      </When>
      <When condition={isProfileReady && drawer === CANDIDATE_DRAWERS.AddHiringAuthority}>
        <AddHiringAuthority
          candidate={candidate}
          onClose={closeDrawer}
          onCompleted={actions.onAddHiringAuthorityCompleted}
        />
      </When>
      <When condition={isProfileReady && drawer === CANDIDATE_DRAWERS.LinkHiringAuthority}>
        <LinkHiringAuthorityDrawer
          candidateId={id}
          onClose={closeDrawer}
          onCompleted={actions.onLinkHiringAuthorityCompleted}
        />
      </When>
      <When condition={drawer === CANDIDATE_DRAWERS.VerifyUrgency && permissions.canVerifyUrgency}>
        <VerifyUrgencyDialog
          entityId={id}
          entityType={EntityType.Candidate}
          open
          onClose={closeDrawer}
          onSaveSucceeded={() => {
            closeDrawer();
            onVerificationCompleted?.();
          }}
        />
      </When>
      <When
        condition={
          isProfileReady &&
          drawer === CANDIDATE_DRAWERS.Presentation &&
          permissions.canUsePresentationMaker
        }
      >
        <PresentationMakerTemplate
          buildSourceParamsFn={buildPresentationMakerCandidateParam}
          generatePresentationFn={generateCandidatePresentation}
          leftContent={({ onSelectPresentationType, isEnabled }) => (
            <CandidatePresentationTypeSelector
              candidate={candidate}
              isEnabled={isEnabled}
              onSelectPresentationType={onSelectPresentationType}
            />
          )}
          onClose={closeDrawer}
          sourceData={candidate}
        />
      </When>
      <When
        condition={
          isProfileReady && drawer === CANDIDATE_DRAWERS.Roleplay && permissions.canUseRoleplay
        }
      >
        <PresentationMakerTemplate
          buildSourceParamsFn={buildPresentationMakerCandidateParam}
          generatePresentationFn={generateCandidatePresentation}
          leftContent={({ onSelectPresentationType }) => (
            <CandidateRoleplayScenarioSelector
              candidate={candidate}
              onSelectPresentationType={onSelectPresentationType}
            />
          )}
          onClose={closeDrawer}
          rightContentFooter={() => <CallVerbiages callType={CALL_VERBIAGES_TYPES.marketing.id} />}
          sourceData={candidate}
          subtitle={strings.ai.generators.roleplay.subtitle}
          title={strings.ai.generators.roleplay.title}
        />
      </When>
      <When condition={drawer === CANDIDATE_DRAWERS.Task && permissions.canUseDailyPlan}>
        <CreationTaskDrawer
          onClose={closeDrawer}
          entityData={{ id: candidate.id, name: candidate.personalInformation?.full_name }}
          entityType={EntityType.Candidate}
          onTaskCreated={actions.openDailyPlan}
        />
      </When>
      <When condition={drawer === CANDIDATE_DRAWERS.Claim && permissions.canClaimAccountability}>
        <ClaimAccountabilityDrawer
          entityId={id}
          entityType={CANDIDATE_PROFILE_CONFIG.id}
          onClose={closeDrawer}
          onSuccess={closeAndRefresh}
        />
      </When>
      <When condition={restrictions.drawerProps.open}>
        <ProfileRestrictionDrawer
          entityId={id}
          entityTypeId={CANDIDATE_PROFILE_CONFIG.entityTypeId}
          onClose={restrictions.drawerProps.onClose}
          open={restrictions.drawerProps.open}
          selectedRestrictionType={restrictions.drawerProps.selectedRestrictionType}
        />
      </When>
      <When condition={isProfileReady && Boolean(candidate.shouldAskRecruiterAddEmployer)}>
        <LinkCompanyDialog type="candidate" entity={candidate} onSetProfile={refresh} />
      </When>
      <SearchProjectForms />
    </>
  );
};
